import React, { memo, useMemo } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { QueryStatsOutlined } from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const AISeasonalChart = memo(({ data = [], generatedAt = '' }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';
  const points = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return rows
      .map((point, index) => ({
        id: firstDefined(point.id, index),
        period: firstDefined(point.period, point.month, point.label, point.date, String(index + 1)),
        historical: Number(firstDefined(point.historical, point.historical_demand, point.historicalDemand)),
        forecast: Number(firstDefined(point.forecast, point.predicted, point.predicted_demand, point.aiPrediction)),
      }))
      .filter((point) => Number.isFinite(point.historical) || Number.isFinite(point.forecast));
  }, [data]);

  if (!points.length) return null;

  const generatedLabel = generatedAt && !Number.isNaN(new Date(generatedAt).getTime())
    ? new Date(generatedAt).toLocaleString(locale)
    : '';

  return (
    <Card variant="outlined" component="section" aria-labelledby="seasonal-forecast-title">
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography id="seasonal-forecast-title" variant="h6" component="h3" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            <QueryStatsOutlined sx={{ color: '#1565c0', mr: 1 }} aria-hidden="true" />
            {t('insights.seasonalForecast')}
          </Typography>
          {generatedLabel && (
            <Typography variant="caption" color="text.secondary">
              {t('insights.updatedAt', { date: generatedLabel })}
            </Typography>
          )}
        </Stack>
        <Box
          role="img"
          aria-label={t('insights.seasonalChartAria')}
          sx={{ width: '100%', height: { xs: 260, sm: 340 } }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 8, right: 12, left: -12, bottom: 8 }}>
              <defs>
                <linearGradient id="insightsHistoricalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1565c0" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#1565c0" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="insightsForecastGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => [
                  Number(value).toLocaleString(locale),
                  name === 'historical' ? t('insights.historical') : t('insights.forecast'),
                ]}
              />
              <Legend
                formatter={(value) => value === 'historical' ? t('insights.historical') : t('insights.forecast')}
              />
              <Area
                type="monotone"
                dataKey="historical"
                stroke="#1565c0"
                strokeWidth={2}
                fill="url(#insightsHistoricalGradient)"
                connectNulls
              />
              <Area
                type="monotone"
                dataKey="forecast"
                stroke="#2e7d32"
                strokeWidth={3}
                fill="url(#insightsForecastGradient)"
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        <Box component="ul" sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)' }}>
          {points.map((point) => (
            <li key={point.id}>
              {t('insights.seasonalPointAria', {
                period: point.period,
                historical: Number.isFinite(point.historical) ? point.historical : t('insights.notAvailable'),
                forecast: Number.isFinite(point.forecast) ? point.forecast : t('insights.notAvailable'),
              })}
            </li>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
});

AISeasonalChart.displayName = 'AISeasonalChart';

export default AISeasonalChart; 